import { useMemo } from "react";
import { Link } from "react-router-dom";
import Chart from "react-apexcharts";
import { FiHome, FiDollarSign, FiShoppingBag, FiClock } from "react-icons/fi";
import UserDataFetcher from "../../../libs/getUserbyUserId";
import useMerchantOrders from "../../../Routes/Marketplace/libs/useMerchantOrders";
import useMerchantStats from "../../../Routes/Marketplace/libs/useMerchantStats";
import PageHeader from "../../components/PageHeader";
import StatCard from "../../components/StatCard";
import DataTable from "../../components/DataTable";
import StatusBadge from "../../components/StatusBadge";
import { getMerchantOrderTotal, getMerchantItems } from "../utils/orderHelpers";

const MerchantOverviewPage = () => {
  const { userData } = UserDataFetcher();
  const merchantId = userData?.user?.userId || userData?.user?._id;
  const { data: orders = [], isLoading, isError } = useMerchantOrders();
  const { data: statsData, isLoading: statsLoading } = useMerchantStats(30);
  const isVerified = userData?.user?.isInfoVerify;

  const stats = useMemo(() => {
    const paidOrders = orders.filter((o) => o.paymentStatus === "paid");
    const totalSales = paidOrders.reduce(
      (sum, o) => sum + getMerchantOrderTotal(o, merchantId),
      0
    );
    const pending = orders.filter(
      (o) => !["Delivered", "Canceled"].includes(o.status)
    ).length;
    return { totalSales, totalOrders: orders.length, pending };
  }, [orders, merchantId]);

  const chartOptions = {
    chart: { toolbar: { show: false } },
    xaxis: { categories: statsData?.dailyRevenue?.map((d) => d.date.slice(5)) || [] },
    colors: ["#284697"],
    stroke: { curve: "smooth" },
  };
  const chartSeries = [{
    name: "Revenue (NGN)",
    data: statsData?.dailyRevenue?.map((d) => d.amount) || [],
  }];

  const columns = [
    { key: "orderNumber", label: "Order #" },
    {
      key: "customer",
      label: "Customer",
      render: (row) => `${row.user?.first_name || ""} ${row.user?.last_name || ""}`.trim() || "Guest",
    },
    {
      key: "items",
      label: "Your items",
      render: (row) => getMerchantItems(row, merchantId).map((i) => i.name).join(", ") || "—",
    },
    {
      key: "total",
      label: "Your total",
      render: (row) => `NGN ${getMerchantOrderTotal(row, merchantId).toLocaleString()}`,
    },
    { key: "status", label: "Status", render: (row) => <StatusBadge status={row.status} /> },
    {
      key: "date",
      label: "Date",
      render: (row) => new Date(row.createdAt).toLocaleDateString(),
    },
  ];

  return (
    <>
      {!isVerified && (
        <div className="dashboard-banner">
          <p>Complete your business information to start selling on the marketplace.</p>
          <Link to="/merchant/account">Complete profile</Link>
        </div>
      )}
      <PageHeader
        title="Overview"
        subtitle="Your store performance at a glance"
        icon={FiHome}
        actions={
          <Link to="/merchant/products/new" className="dashboard-btn dashboard-btn--primary">
            Add product
          </Link>
        }
      />
      <div className="dashboard-stat-grid">
        <StatCard label="Sales (paid)" value={`NGN ${stats.totalSales.toLocaleString()}`} icon={FiDollarSign} />
        <StatCard label="Total orders" value={stats.totalOrders} icon={FiShoppingBag} />
        <StatCard label="Pending orders" value={stats.pending} icon={FiClock} />
      </div>
      <div className="dashboard-card">
        <h2 className="dashboard-card__title">Revenue (last 30 days)</h2>
        {statsLoading ? (
          <p>Loading chart…</p>
        ) : chartSeries[0].data.some((v) => v > 0) ? (
          <Chart options={chartOptions} series={chartSeries} type="area" height={280} />
        ) : (
          <p style={{ color: "var(--dash-muted)" }}>No paid orders in this period yet.</p>
        )}
      </div>
      <div className="dashboard-card">
        <h2 className="dashboard-card__title">Recent orders</h2>
        {isError ? (
          <p style={{ color: "var(--dash-danger)" }}>Failed to load orders.</p>
        ) : (
          <DataTable
            columns={columns}
            rows={orders.slice(0, 8).map((o) => ({ ...o, _key: o._id }))}
            loading={isLoading}
            emptyMessage="No orders yet"
          />
        )}
      </div>
    </>
  );
};

export default MerchantOverviewPage;
